import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Typography,
  Row,
  Col,
  Tag,
  Divider,
  Button,
  Avatar,
  theme,
  Space,
  Flex,
  Card,
  Descriptions,
  Badge,
  Statistic,
  Select,
  InputNumber,
  Tooltip,
  Input,
  Popconfirm,
} from "antd";
import {
  FilePdfOutlined,
  DownloadOutlined,
  EyeOutlined,
  MailOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  UserOutlined,
  CalendarOutlined,
  NumberOutlined,
  FileTextOutlined,
  EditOutlined,
  CloseOutlined,
  DeleteOutlined,
  LoadingOutlined,
  CheckCircleFilled,
  FileProtectOutlined,
} from "@ant-design/icons";
import { URL_FILES } from "../../../constans/apis";
import { downloadFileFetch, getPdfUrlFetch } from "../../../lib/files/api/fetchFile";
import { useUpdateQuoteStatus } from "../hooks/useUpdateQuoteStatus";
import { useDeleteQuoteProduct } from "../hooks/useDeleteQuoteProduct";
import { useDeleteQuote } from "../hooks/useDeleteQuote";
import type { QuoteData, QuoteProduct } from "../types";
import openNotification from "../../../utils/openNotification";
import { HybridCard, useHybridResponsive } from "../../shared";
import dayjs from "dayjs";
import { useFetchClientById } from "../../clients/hooks/useFetchClientById";
import SkeletonNode from "../../shared/components/SkeletonNode";
import FormNewProduct from "./FormNewProduct";
import FormEditClient from "./FormEditClient";
import { useUpdateQuoteClient } from "../hooks/useUpdateQuoteClient";
import type { ClientData } from "../../clients/types";
import type { ProductData } from "../../products/types";
import { useAddNewProduct } from "../hooks/useAddNewProduct";
import { ReceiptText, Hash, Coins, AlignLeft, SlidersHorizontal } from "lucide-react";
import { useUpdateQuoteProduct } from "../hooks/useUpdateQuoteProduct";
import { useNavigate } from "react-router-dom";
import EditQuotePanel from "./EditQuotePanel";
import ItemProduct from "../../products/components/ItemProduct";
import PdfEditor from "../../shared/PdfEditor/PdfEditor";
import QuotePdfDocument from "./QuotePdfDocument";
import { useFetchCompany } from "../../company/hooks/useFetchCompany";
import { useCompanyLogoImage } from "../../company/hooks/useCompanyLogoImage";
import { useQuoteSignatureImage } from "../hooks/useQuoteSignatureImage";
import { useUpdateQuote } from "../hooks/useUpdateQuote";
import { calcQuoteTotals, lineSubtotal, resolveQuoteTotals, totalsAreStale } from "../utils/quoteTotals";

const { Text, Title } = Typography;

// ---------------------------------------------------------------------------
// Helper: format date
// ---------------------------------------------------------------------------
function formatDate(dateStr?: string): string {
  if (!dateStr) return "—";
  return dayjs(dateStr).format("DD MMM YYYY");
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface QuoteDetailsProps {
  quote: QuoteData;
  onDeleted?: () => void;
}

// ---------------------------------------------------------------------------
// Section label style (matches ContractDetails pattern)
// ---------------------------------------------------------------------------
const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 1,
  fontWeight: 700,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const QuoteDetails: React.FC<QuoteDetailsProps> = ({ quote, onDeleted }) => {
  const { token } = theme.useToken();
  const { isMobile } = useHybridResponsive();
  const navigate = useNavigate();

  const [downloading, setDownloading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [localStatus, setLocalStatus] = useState(quote.status_quote);
  const [localQuote, setLocalQuote] = useState<QuoteData>(quote);
  const [editMode, setEditMode] = useState(false);
  const [showFormClient, setShowFormClient] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);

  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateQuoteStatus();
  const { mutate: removeQuote, isPending: isDeletingQuote } = useDeleteQuote();
  const { data: client, isLoading: loadingClient } = useFetchClientById(localQuote.id_client);
  const { data: company } = useFetchCompany();
  const { data: companyLogoSrc } = useCompanyLogoImage(company?.uuid_logo);
  const { data: signatureSrc } = useQuoteSignatureImage(localQuote.uuid_signature_quote);
  const { mutate: updateClient, isPending: isUpdatingClient } = useUpdateQuoteClient();
  const { mutate: updateQuote } = useUpdateQuote();

  // Importes derivados de las líneas: es lo que se muestra y lo que va al PDF.
  const totals = resolveQuoteTotals(localQuote);

  const allowEdit = localStatus === "pending";
  // Un contrato solo puede originarse de una cotización ya cerrada.
  const allowCreateContract = localStatus === "completed";
  // Deletion is restricted to rejected quotes — the backend enforces the same rule.
  const allowDelete = localStatus === "rejected";

  useEffect(() => {
    setLocalStatus(quote.status_quote);
    setLocalQuote(quote);
    setEditMode(false);
    setShowFormClient(false);
    setShowPdfPreview(false);
  }, [quote]);

  useEffect(() => {
    return () => {
      if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Status config
  const statusTokenMap = {
    pending: { antColor: "warning" as const, dotStatus: "warning" as const, icon: <ClockCircleOutlined />, tokenColor: token.colorWarning, tokenBg: token.colorWarningBg },
    completed: { antColor: "success" as const, dotStatus: "success" as const, icon: <CheckCircleOutlined />, tokenColor: token.colorSuccess, tokenBg: token.colorSuccessBg },
    rejected: { antColor: "error" as const, dotStatus: "error" as const, icon: <CloseCircleOutlined />, tokenColor: token.colorError, tokenBg: token.colorErrorBg },
  };
  const cfg = statusTokenMap[localStatus] ?? statusTokenMap.pending;

  const handleDownload = async () => {
    if (!localQuote.uuid_file_quote) {
      openNotification("warning", "Atención", "No hay documento adjunto.");
      return;
    }
    setDownloading(true);
    try {
      // @ts-ignore
      await downloadFileFetch(`${URL_FILES}/${localQuote.uuid_file_quote}`, typeof TOKEN_FILES !== "undefined" ? TOKEN_FILES : undefined);
    } catch {
      openNotification("error", "Error", "Error al descargar el documento.");
    } finally {
      setDownloading(false);
    }
  };

  const handleView = async () => {
    if (!localQuote.uuid_file_quote) {
      openNotification("warning", "Atención", "No hay documento adjunto.");
      return;
    }
    if (pdfUrl) { setPdfUrl(null); return; }
    setViewing(true);
    try {
      // @ts-ignore
      const url = await getPdfUrlFetch(`${URL_FILES}/${localQuote.uuid_file_quote}`, typeof TOKEN_FILES !== "undefined" ? TOKEN_FILES : undefined);
      setPdfUrl(url);
    } catch {
      openNotification("error", "Error", "Error al cargar el documento.");
    } finally {
      setViewing(false);
    }
  };

  const handleStatusChange = (newStatus: string) => {
    updateStatus(
      { id_quote: localQuote.id_quote, status_quote: newStatus },
      {
        onSuccess: () => {
          setLocalStatus(newStatus as any);
          setLocalQuote((prev) => ({ ...prev, status_quote: newStatus as any }));
        },
        onError: (err) => openNotification("error", "Error", err.message),
      }
    );
  };

  const handleDeleteQuote = () => {
    removeQuote(localQuote.id_quote, {
      onSuccess: () => {
        openNotification("success", "Éxito", "Cotización eliminada correctamente");
        onDeleted?.();
      },
      onError: (err) => openNotification("error", "Error", err.message),
    });
  };

  const handleSaved = (updated: Partial<QuoteData>) => {
    setLocalQuote((prev) => ({ ...prev, ...updated }));
  };

  /**
   * Las líneas son la fuente de verdad de los importes: al cambiarlas se
   * recalculan subtotal/IVA/total, se refleja en pantalla y en el PDF, y se
   * persisten en la cotización para que el listado y las estadísticas cuadren.
   */
  const handleProductsChange = (products: QuoteProduct[]) => {
    const nextTotals = calcQuoteTotals(products);
    setLocalQuote((prev) => ({
      ...prev,
      products,
      total_quote: nextTotals.total,
      iva_quote: nextTotals.iva,
    }));

    if (!totalsAreStale(localQuote, nextTotals)) return;

    updateQuote(
      {
        id_quote: localQuote.id_quote,
        id_client: localQuote.id_client,
        id_contract: localQuote.id_contract,
        description_quote: localQuote.description_quote,
        quote_wave: localQuote.quote_wave,
        date_quote: dayjs(localQuote.date_quote).format("YYYY-MM-DD"),
        date_expiration: dayjs(localQuote.date_expiration).format("YYYY-MM-DD"),
        uuid_file_quote: localQuote.uuid_file_quote,
        total_quote: nextTotals.total,
        iva_quote: nextTotals.iva,
      },
      {
        onError: (err) =>
          openNotification("error", "Error al actualizar los totales", err.message),
      }
    );
  };

  const handleChangeClient = (clientData: ClientData | undefined) => {
    if (!clientData) return;
    if (clientData.id_client === localQuote.id_client) {
      setShowFormClient(false);
      return;
    }
    setShowFormClient(false);
    updateClient(
      { id_quote: localQuote.id_quote, id_client: clientData.id_client },
      {
        onSuccess: () => {
          openNotification("success", "Éxito", "Cliente actualizado correctamente");
          setShowFormClient(false);
          setLocalQuote((prev) => ({ ...prev, id_client: clientData.id_client }));
        },
        onError: (err) => {
          openNotification("error", "Error", err.message)
          setShowFormClient(true);
        },
      }
    );
  };

  const statusOptionLabel = (opt: { color: "warning" | "success" | "error"; label: string }) => (
    <Flex align="center" gap={6}>
      <Badge status={opt.color} /> {opt.label}
    </Flex>
  );

  const actionsRow = (
    <Flex
      gap={8}
      align="center"
      wrap
      style={{
        width: isMobile ? "100%" : undefined,
        justifyContent: isMobile ? "flex-end" : "flex-start",
      }}
    >
      {/* Crear contrato – solo para cotizaciones completadas */}
      {allowCreateContract && (
        <Tooltip title="Crear un contrato a partir de esta cotización">
          <Button
            size="small"
            type="primary"
            icon={<FileProtectOutlined />}
            onClick={() =>
              navigate(`/contrato/nuevo?id_quote=${localQuote.id_quote}`)
            }
          >
            Crear contrato
          </Button>
        </Tooltip>
      )}

      {/* Status selector — doubles as the status indicator, no duplicate tag needed */}
      <Select
        value={localStatus}
        onChange={handleStatusChange}
        loading={isUpdatingStatus}
        size="small"
        optionLabelProp="label"
        style={{
          width: 150,
          fontWeight: 700,
          backgroundColor: cfg.tokenBg,
          borderRadius: 999,
        }}
      >
        <Select.Option value="pending" label={statusOptionLabel({ color: "warning", label: "Pendiente" })}>
          {statusOptionLabel({ color: "warning", label: "Pendiente" })}
        </Select.Option>
        <Select.Option value="completed" label={statusOptionLabel({ color: "success", label: "Completada" })}>
          {statusOptionLabel({ color: "success", label: "Completada" })}
        </Select.Option>
        <Select.Option value="rejected" label={statusOptionLabel({ color: "error", label: "Rechazada" })}>
          {statusOptionLabel({ color: "error", label: "Rechazada" })}
        </Select.Option>
      </Select>

      {/* Delete – only for rejected quotes */}
      {allowDelete && (
        <Popconfirm
          title="Eliminar cotización"
          description="¿Estás seguro de que quieres eliminar esta cotización?"
          okText="Sí, eliminar"
          cancelText="Cancelar"
          okButtonProps={{ danger: true }}
          onConfirm={handleDeleteQuote}
        >
          <Tooltip title="Eliminar cotización">
            <Button
              size="small"
              type="primary"
              ghost
              danger
              icon={<DeleteOutlined />}
              loading={isDeletingQuote}
            />
          </Tooltip>
        </Popconfirm>
      )}
    </Flex>
  );

  return (
    <Row gutter={[20, 20]}>
      <Col span={24}>

        {/* ── Hero card ─────────────────────────────────────────────────── */}
        <Card
          style={{
            marginBottom: 16,
            background: `linear-gradient(135deg, ${token.colorBgContainer} 0%, ${cfg.tokenBg} 100%)`,
            border: `1px solid ${token.colorBorderSecondary}`,
          }}
          styles={{ body: { padding: 24 } }}
        >
          <Flex vertical={isMobile} gap={16}>
            <Flex gap={16} align="flex-start">
              <Avatar
                size={64}
                icon={<DollarOutlined />}
                shape="square"
                style={{
                  borderRadius: token.borderRadiusLG,
                  background: cfg.tokenBg,
                  color: cfg.tokenColor,
                  fontSize: 26,
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Space size={6} wrap style={{ marginBottom: 6 }}>
                  {localQuote.id_quote && (
                    <Tag style={{ borderRadius: 999, background: token.colorFillAlter, border: "none" }}>
                      #{localQuote.id_quote}
                    </Tag>
                  )}

                  {/* Edit toggle – only for pending quotes */}
                  {allowEdit && (
                    <Tooltip title={editMode ? "Cancelar edición" : "Editar cotización"}>
                      <Button
                        size="small"
                        type="text"
                        icon={editMode ? <CloseOutlined /> : <EditOutlined />}
                        onClick={() => setEditMode((v) => !v)}
                        danger={editMode}
                      />
                    </Tooltip>
                  )}
                </Space>
                <Title level={4} style={{ margin: 0, fontWeight: 800 }}>
                  {MXN.format(totals.total)}
                </Title>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Subtotal: {MXN.format(totals.subtotal)} · IVA: {MXN.format(totals.iva)}
                </Text>
              </div>
              {!isMobile && actionsRow}
            </Flex>
            {isMobile && (
              <>
                <Divider style={{ margin: 0, borderColor: token.colorBorderSecondary }} />
                {actionsRow}
              </>
            )}
          </Flex>
        </Card>


        {/* ── Edit panel (replaces read-only sections when active) ─────── */}
        {editMode ? (
          <EditQuotePanel
            quote={localQuote}
            onCancel={() => setEditMode(false)}
            onSaved={handleSaved}
          />
        ) : (
          <>
            {/* ── Client ────────────────────────────────────────────────── */}
            <Card
              size="small"
              title={<Flex justify="space-between" align="center">
                <Text type="secondary" style={sectionLabelStyle}>Cliente</Text>
                {allowEdit && (
                  <Button
                    type="text"
                    icon={showFormClient ? <CloseOutlined /> : <EditOutlined />}
                    onClick={() => setShowFormClient(!showFormClient)}
                    danger={showFormClient}
                  />
                )}
              </Flex>}
              style={{ marginBottom: 16, border: `1px solid ${token.colorBorderSecondary}` }}
            >
              {showFormClient ? (
                <FormEditClient
                  currentClientId={localQuote.id_client}
                  handleChangeClient={handleChangeClient}
                />
              ) : loadingClient || isUpdatingClient ? (
                <SkeletonNode rows={2} />
              ) : client ? (
                <Flex gap={12} align="center">
                  <Avatar
                    icon={<UserOutlined />}
                    style={{ background: token.colorPrimaryBg, color: token.colorPrimary, flexShrink: 0 }}
                  />
                  <div>
                    <Text strong style={{ display: "block" }}>{client.client_name}</Text>
                    {client.client_email && (
                      <Flex align="center" gap={4}>
                        <MailOutlined style={{ fontSize: 11, color: token.colorTextTertiary }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>{client.client_email}</Text>
                      </Flex>
                    )}
                    {client.client_phone && (
                      <Flex align="center" gap={4}>
                        <PhoneOutlined style={{ fontSize: 11, color: token.colorTextTertiary }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>{client.client_phone}</Text>
                      </Flex>
                    )}
                  </div>
                </Flex>
              ) : (
                <Text type="secondary">No se encontró información del cliente.</Text>
              )}
            </Card>

            {/* ── Quote dates & description ────────────────────────────── */}
            <Card
              size="small"
              title={<Text type="secondary" style={sectionLabelStyle}>Detalles de la Cotización</Text>}
              style={{ marginBottom: 16, border: `1px solid ${token.colorBorderSecondary}` }}
            >
              <Descriptions column={{ xs: 1, sm: 2 }} size="small">
                <Descriptions.Item
                  label={
                    <Flex align="center" gap={4}>
                      <CalendarOutlined />
                      <span>Fecha Cotización</span>
                    </Flex>
                  }
                >
                  <Text strong>{formatDate(localQuote.date_quote)}</Text>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Flex align="center" gap={4}>
                      <ClockCircleOutlined />
                      <span>Fecha Expiración</span>
                    </Flex>
                  }
                >
                  <Text strong>{formatDate(localQuote.date_expiration)}</Text>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Flex align="center" gap={4}>
                      <FileTextOutlined />
                      <span>Descripción</span>
                    </Flex>
                  }
                  span={2}
                >
                  <Text>{localQuote.description_quote || "—"}</Text>
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <Flex align="center" gap={4}>
                      <FileTextOutlined />
                      <span>Texto Presentación</span>
                    </Flex>
                  }
                  span={2}
                >
                  <Text>{localQuote.quote_wave || "—"}</Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* ── Concepts ──────────────────────────────────────────────── */}
            {localQuote.concepts && localQuote.concepts.length > 0 && (
              <Card
                size="small"
                title={<Text type="secondary" style={sectionLabelStyle}>Conceptos</Text>}
                style={{ marginBottom: 16, border: `1px solid ${token.colorBorderSecondary}` }}
              >
                <Flex vertical gap={8}>
                  {localQuote.concepts.map((concept) => (
                    <HybridCard
                      key={concept.id_concept}
                      title={concept.concept_name}
                      icon={<ReceiptText size={16} />}
                    />
                  ))}
                </Flex>
              </Card>
            )}

            {/* ── Products ──────────────────────────────────────────────── */}
            <ProductsSection
              quote={localQuote}
              allowEdit={allowEdit}
              products={localQuote.products ?? []}
              onProductsChange={handleProductsChange}
            />

            {/* ── System references ─────────────────────────────────────── */}
            <Card
              size="small"
              title={<Text type="secondary" style={sectionLabelStyle}>Referencias del Sistema</Text>}
              style={{ marginBottom: 16, border: `1px solid ${token.colorBorderSecondary}` }}
            >
              <Row gutter={[8, 8]}>
                {[
                  { label: "Cotización", value: localQuote.id_quote },
                  { label: "Cliente", value: localQuote.id_client },
                  { label: "Empresa", value: localQuote.id_company },
                  { label: "Contrato", value: localQuote.id_contract },
                ].map(({ label, value }) => (
                  <Col xs={12} sm={6} key={label}>
                    <Card
                      size="small"
                      styles={{ body: { textAlign: "center", padding: "12px 8px" } }}
                      style={{ background: token.colorFillAlter, border: "none" }}
                    >
                      <Statistic
                        title={<Text style={{ fontSize: 11 }} type="secondary">{label}</Text>}
                        value={value ?? "—"}
                        valueStyle={{ color: token.colorPrimary, fontSize: 20, fontWeight: 700 }}
                        prefix={<NumberOutlined style={{ fontSize: 12 }} />}
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
            {/* ── PDF preview (cotización completa) ─────────────────────── */}
            <Card
              size="small"
              title={<Text type="secondary" style={sectionLabelStyle}>Vista Previa de la Cotización</Text>}
              extra={
                <Button
                  size="small"
                  type={showPdfPreview ? "default" : "primary"}
                  icon={<FilePdfOutlined />}
                  onClick={() => setShowPdfPreview((v) => !v)}
                >
                  {showPdfPreview ? "Ocultar PDF" : "Generar PDF"}
                </Button>
              }
              style={{ marginBottom: 16, border: `1px solid ${token.colorBorderSecondary}` }}
              styles={{ body: showPdfPreview ? { padding: 0 } : undefined }}
            >
              {showPdfPreview ? (
                <div
                  style={{
                    height: isMobile ? 520 : 760,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <PdfEditor
                    readOnly
                    typeDocument="Cotización"
                    documentTitle={`cotizacion-${localQuote.id_quote}`}
                    document={<QuotePdfDocument quote={localQuote} client={client} company={company} logoSrc={companyLogoSrc} signatureSrc={signatureSrc} />}
                  />
                </div>
              ) : (
                <Flex align="center" gap={14}>
                  <Avatar
                    size={44}
                    icon={<FilePdfOutlined />}
                    shape="square"
                    style={{
                      background: token.colorErrorBg,
                      color: token.colorError,
                      borderRadius: token.borderRadius,
                      flexShrink: 0,
                    }}
                  />
                  <div>
                    <Text strong style={{ display: "block" }}>Documento generado de la cotización</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Vista previa con todos los datos y descarga en PDF.
                    </Text>
                  </div>
                </Flex>
              )}
            </Card>

            <Divider style={{ margin: "8px 0 16px" }} />

            {/* ── Document card ─────────────────────────────────────────── */}
            <Card
              size="small"
              title={<Text type="secondary" style={sectionLabelStyle}>Documento Adjunto</Text>}
              extra={
                <Space size={8}>
                  {!isMobile && (
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={handleView}
                      loading={viewing}
                      disabled={downloading}
                    >
                      {pdfUrl ? "Ocultar" : "Ver"}
                    </Button>
                  )}
                  <Button
                    size="small"
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownload}
                    loading={downloading}
                    disabled={!localQuote.uuid_file_quote}
                  >
                    Descargar
                  </Button>
                </Space>
              }
              style={{ border: `1px dashed ${token.colorBorder}`, background: token.colorBgLayout }}
            >
              <Flex align="center" gap={14}>
                <Avatar
                  size={44}
                  icon={<FilePdfOutlined />}
                  shape="square"
                  style={{
                    background: token.colorErrorBg,
                    color: token.colorError,
                    borderRadius: token.borderRadius,
                    flexShrink: 0,
                  }}
                />
                <div>
                  <Text strong style={{ display: "block" }}>Archivo de Cotización</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {localQuote.uuid_file_quote ? "Formato PDF o XML" : "Sin documento adjunto"}
                  </Text>
                </div>
              </Flex>

              {pdfUrl && (
                <div
                  style={{
                    marginTop: 16,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadius,
                    overflow: "hidden",
                  }}
                >
                  <iframe
                    src={pdfUrl}
                    title="Visor Adjunto"
                    width="100%"
                    height="600"
                    style={{ border: "none", display: "block" }}
                  />
                </div>
              )}
            </Card>
          </>
        )}

      </Col>
    </Row>
  );
};

// ---------------------------------------------------------------------------
// ProductsSection
// ---------------------------------------------------------------------------
const ProductsSection = ({
  quote,
  allowEdit,
  products,
  onProductsChange,
}: {
  quote: QuoteData;
  allowEdit: boolean;
  products: QuoteProduct[];
  /** Notifica al padre la nueva lista de líneas para recalcular los importes. */
  onProductsChange: (products: QuoteProduct[]) => void;
}) => {
  const { mutate: addNewProduct, isPending: isAddingProduct } = useAddNewProduct();
  const [showFormProduct, setShowFormProduct] = useState(false);
  const { mutate: deleteProduct, isPending: isDeletingProduct } = useDeleteQuoteProduct();

  // Las respuestas de las mutaciones llegan en diferido: se parte siempre de la
  // última lista conocida para no pisar cambios de otras líneas.
  const productsRef = useRef(products);
  useEffect(() => {
    productsRef.current = products;
  }, [products]);

  const handleDeleteProduct = (id_quote_product: number | undefined, id_product: number | undefined) => {
    if (!id_quote_product) {
      onProductsChange(productsRef.current.filter((item) => item.id_product !== id_product));
      return;
    }
    deleteProduct(id_quote_product, {
      onSuccess: () => {
        openNotification("success", "Éxito", "Producto eliminado correctamente");
        onProductsChange(
          productsRef.current.filter((item) => item.id_quote_product !== id_quote_product)
        );
      },
      onError: (err) => openNotification("error", "Error", err.message),
    });
  };

  const handleChangeProduct = (data: ProductData | undefined) => {
    if (!data) return;
    const payload: QuoteProduct = {
      product_name: data.product_name,
      product_price: data.product_price,
      product_amount: 1,
      // El precio de la línea parte del precio de venta del catálogo.
      product_price_sale: data.product_price_sale ?? data.product_price,
      product_description: data.product_description || data.product_name,
      product_type: data.product_type,
      product_iva: data.product_iva,
      product_iva_percentage: data.product_iva_percentage,
      id_product: data.id_product,
      id_quote: quote.id_quote,
    };
    addNewProduct(payload, {
      onSuccess: (res) => {
        openNotification("success", "Éxito", "Producto agregado correctamente");
        setShowFormProduct(false);
        // El backend devuelve el id de la línea creada: sin él no se puede editar.
        const created = res?.data as { id?: number } | undefined;
        const id_quote_product = Number(created?.id) || undefined;
        onProductsChange([...productsRef.current, { ...payload, id_quote_product }]);
      },
      onError: (err) => openNotification("error", "Error", err.message),
    });
  };

  /** Aplica los cambios de una línea (cantidad, precio o descripción). */
  const handleProductLineChange = (updated: QuoteProduct) => {
    onProductsChange(
      productsRef.current.map((item) =>
        (item.id_quote_product && item.id_quote_product === updated.id_quote_product) ||
          item.id_product === updated.id_product
          ? updated
          : item
      )
    );
  };


  if (isAddingProduct || isDeletingProduct) {
    return <Card style={{ marginBottom: 12 }}><SkeletonNode rows={1} /></Card>;
  }

  return (
    <>
      <Card
        style={{ marginBottom: 12 }}
        title={<Flex justify="space-between" align="center">
          <Text type="secondary" style={sectionLabelStyle}>
            {showFormProduct ? "Agregar Producto" : "Productos"}
          </Text>
          <Button
            disabled={!allowEdit}
            icon={<PlusOutlined />}
            onClick={() => setShowFormProduct(!showFormProduct)}
            danger={showFormProduct}
            type="primary"
            size="small"
          >
            {showFormProduct ? "Cancelar" : "Agregar"}
          </Button>
        </Flex>}
      >

        {showFormProduct ? (
          <FormNewProduct handleChangeProduct={handleChangeProduct} selectedProducts={products} />
        ) : (
          <Flex vertical gap={8}>
            {/* Lo último agregado se muestra hasta arriba; el arreglo original
                conserva el orden de captura para el PDF y los importes. */}
            {[...products].reverse().map((product) => (
              <ItemProduct
                key={product.id_quote_product ?? product.id_product}
                product={product}
                footer={
                  <FooterItemProduct
                    product={product}
                    allowEdit={allowEdit}
                    handleDeleteProduct={handleDeleteProduct}
                    onProductChange={handleProductLineChange}
                  />
                }
              />
            ))}
          </Flex>
        )}
      </Card>
    </>
  );
};

export default QuoteDetails;

const MXN = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });

function FooterItemProduct({
  product,
  allowEdit = true,
  handleDeleteProduct,
  onProductChange,
}: {
  product: QuoteProduct;
  allowEdit?: boolean;
  handleDeleteProduct: (id_quote_product: number | undefined, id_product: number | undefined) => void;
  /** Se dispara al guardar la línea para que el detalle y el PDF se actualicen. */
  onProductChange: (product: QuoteProduct) => void;
}) {
  const { token } = theme.useToken();
  const { isMobile } = useHybridResponsive();
  const { mutate: updateProduct } = useUpdateQuoteProduct();

  const [amount, setAmount] = useState<number>(Number(product.product_amount) || 1);
  const [price, setPrice] = useState<number>(Number(product.product_price_sale) || product.product_price);
  const [description, setDescription] = useState<string>(product.product_description ?? "");
  const [phase, setPhase] = useState<"idle" | "saving" | "saved">("idle");

  const savedRef = useRef({ amount, price, description });

  const importe = lineSubtotal({ ...product, product_amount: amount, product_price_sale: price });
  const isDirty =
    amount !== savedRef.current.amount ||
    price !== savedRef.current.price ||
    description !== savedRef.current.description;

  const canSave = allowEdit && !!product.id_quote_product;

  // Debounced autosave — commit once the user pauses, never per keystroke.
  useEffect(() => {
    if (!canSave || !isDirty) return;
    const t = setTimeout(() => {
      setPhase("saving");
      updateProduct(
        {
          id_quote: product.id_quote,
          id_quote_product: product.id_quote_product!,
          product_amount: amount,
          product_price_sale: price,
          product_description: description,
        },
        {
          onSuccess: () => {
            savedRef.current = { amount, price, description };
            setPhase("saved");
            onProductChange({
              ...product,
              product_amount: amount,
              product_price_sale: price,
              product_description: description,
            });
          },
          onError: (err) => {
            setPhase("idle");
            openNotification("error", "Error", err.message);
          },
        }
      );
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, price, description, canSave, isDirty]);

  // Let the "Guardado" badge fade away on its own.
  useEffect(() => {
    if (phase !== "saved") return;
    const t = setTimeout(() => setPhase("idle"), 1800);
    return () => clearTimeout(t);
  }, [phase]);

  const microLabel: React.CSSProperties = {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: 1,
    fontWeight: 700,
    color: token.colorTextTertiary,
  };
  const numFont: React.CSSProperties = {
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum"',
  };
  const chip =
    phase === "saving"
      ? { label: "Guardando", color: token.colorInfo, bg: token.colorInfoBg, icon: <LoadingOutlined /> }
      : phase === "saved"
        ? { label: "Guardado", color: token.colorSuccess, bg: token.colorSuccessBg, icon: <CheckCircleFilled /> }
        : isDirty && canSave
          ? {
            label: "Sin guardar",
            color: token.colorWarning,
            bg: token.colorWarningBg,
            icon: <span style={{ width: 6, height: 6, borderRadius: 999, background: token.colorWarning, display: "inline-block" }} />,
          }
          : null;

  return (
    <div style={{ paddingTop: 2 }}>
      {/* Header + autosave status */}
      <Flex justify="space-between" align="center" style={{ marginBottom: 14 }}>
        <Flex align="center" justify="space-between" style={{ width: "100%" }}>
          <section>
            <Flex align="center" gap={6}>
              <SlidersHorizontal size={13} color={token.colorTextTertiary} />
              <Text style={microLabel}>Detalle de línea</Text>
            </Flex>
          </section>


          <AnimatePresence mode="wait">
            {!chip &&
              <Popconfirm
                title="Eliminar producto"
                description="¿Estás seguro de que quieres eliminar este producto?"
                onConfirm={() => handleDeleteProduct(product.id_quote_product, product.id_product)}
                onCancel={() => { }}
              >
                <Button
                  disabled={!allowEdit}
                  type="primary"
                  ghost
                  danger
                >Delete</Button>
              </Popconfirm>
            }
            {chip && (
              <motion.div
                key={chip.label}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.18 }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "2px 10px",
                  borderRadius: 999,
                  fontSize: 11,
                  fontWeight: 700,
                  color: chip.color,
                  background: chip.bg,
                }}
              >
                {chip.icon}
                {chip.label}
              </motion.div>
            )}
          </AnimatePresence>
        </Flex>

      </Flex>

      {/* Quantity · Unit price · Line total */}
      <Flex gap={16} wrap align="flex-end" style={{ marginBottom: 14 }}>
        {/* Quantity stepper */}
        <Flex vertical gap={6}>
          <Flex align="center" gap={5}>
            <Hash size={12} color={token.colorTextTertiary} />
            <Text style={microLabel}>Cantidad</Text>
          </Flex>
          <InputNumber
            value={amount}
            min={1}
            precision={0}
            disabled={!allowEdit}
            onChange={(v) => setAmount(Number(v) || 1)}
            onBlur={() => { if (!amount || amount < 1) setAmount(1); }}
            style={{ width: isMobile ? "100%" : 120, ...numFont }}
          />
        </Flex>

        {/* Unit price */}
        <Flex vertical gap={6} style={{ flex: isMobile ? "1 1 100%" : "0 1 200px" }}>
          <Flex align="center" gap={5}>
            <Coins size={12} color={token.colorTextTertiary} />
            <Text style={microLabel}>Precio venta</Text>
          </Flex>
          <InputNumber
            value={price}
            min={0}
            disabled={!allowEdit}
            controls={false}
            prefix={<span style={{ color: token.colorTextTertiary, fontWeight: 600 }}>$</span>}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={(v) => Number((v || "").replace(/[^\d.]/g, ""))}
            onChange={(v) => setPrice(Number(v) || 0)}
            style={{ width: "100%", ...numFont }}
          />
        </Flex>

        {/* Line total — animates as values change */}
        <Flex
          vertical
          gap={2}
          style={{
            marginLeft: isMobile ? 0 : "auto",
            flex: isMobile ? "1 1 100%" : "0 0 auto",
            alignItems: isMobile ? "flex-start" : "flex-end",
          }}
        >
          <Text style={microLabel}>Importe</Text>
          <div style={{ position: "relative", height: 30, minWidth: 120 }}>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={importe}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                style={{
                  position: "absolute",
                  right: isMobile ? "auto" : 0,
                  left: isMobile ? 0 : "auto",
                  fontSize: 24,
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  lineHeight: "30px",
                  color: token.colorText,
                  whiteSpace: "nowrap",
                  ...numFont,
                }}
              >
                {MXN.format(importe)}
              </motion.span>
            </AnimatePresence>
          </div>
        </Flex>
      </Flex>

      {/* Description */}
      <Flex vertical gap={6}>
        <Flex align="center" gap={5}>
          <AlignLeft size={12} color={token.colorTextTertiary} />
          <Text style={microLabel}>Descripción</Text>
        </Flex>
        <Input.TextArea
          value={description}
          disabled={!allowEdit}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Descripción del producto…"
          autoSize={{ minRows: 1, maxRows: 3 }}
          style={{ resize: "none" }}
        />
      </Flex>
    </div>
  );
}
